import { PostHog } from 'posthog-js';
import { Dictionary } from 'lodash';
import isEqual from 'lodash/isEqual';

export type PostHogCaptureProperties = Dictionary<string | boolean | number | null | Array<PostHogCaptureProperties>>;

export enum PostHogEventNames {
  JOB_FORM_OPENED = 'Job Form Opened',
  JOB_FORM_QUESTIONS_ANSWERED = 'Job Form Questions Answered',
  TEST_EVENT = 'test_event',
  PERSONAL_DETAILS_VIEWED = 'Personal Details Viewed',
  PROS_LIST_VIEWED = 'Pros List Viewed',
  PRO_CARD_VIEWED = 'Pro Card Viewed',
  PRO_PROFILE_VIEWED = 'Pro Profile Viewed',
  VISIT_FEE_VIEWED = 'Visit Fee Viewed',
  EXTRA_PRO_FLOW_TRIGGERED = 'Extra Pro Flow Triggered',
  JOB_CANCEL_BUTTON_CLICKED = 'Job Cancel Button Clicked',
  JOB_UPDATE_BUTTON_CLICKED = 'Job Update Button Clicked',
  NEED_HELP_TRIGGERED = 'Need Help Triggered',
  EXPRESS_YOUR_INTEREST_TRIGGERED = 'Express Your Interest Triggered',
  PAY_ONLINE_TRIGGERED = 'Pay Online Triggered',
  LOCATION_SELECTED = 'Location Selected',
  PHONE_NUMBER_REVEALED = 'Phone Number Revealed',
  SUCCESS_MODAL_SHOWN = 'Success Modal Shown',
  SUCCESS_MODAL_ACTION = 'Success Modal Action',
  SERVICES_SECTION_CLICKED = 'Services Section Clicked',
  // Customer support (#5619). One vocabulary for every support surface — the
  // floating widget and the embedded entry points (exit intent #5622) alike.
  SUPPORT_WIDGET_SHOWN = 'Support Widget Shown',
  SUPPORT_WIDGET_CLICKED = 'Support Widget Clicked',
  SUPPORT_CHANNEL_SELECTED = 'Support Channel Selected',
  SUPPORT_WIDGET_DISMISSED = 'Support Widget Dismissed'
}

export interface PostHogProviderConfig {
  alwaysStoreProperties: boolean;
  isProduction: boolean;
  isDevelopment: boolean;
}

export class PostHogProvider {

  private static instance: PostHogProvider;

  private readonly config: Partial<PostHogProviderConfig> = {
    alwaysStoreProperties: false,
    isProduction: false,
    isDevelopment: false
  };

  private storedProperties: PostHogCaptureProperties = {};

  private capturedCache = new Map();

  static getInstance(ph: PostHog, config?: Partial<PostHogProviderConfig>): PostHogProvider {
    if (!this.instance) {
      this.instance = new PostHogProvider(ph, config);
    }

    return this.instance;
  }

  get shouldBeTriggered(): boolean {
    return this.posthog !== undefined && !this.config.isDevelopment;
  }

  constructor(private posthog: PostHog, config?: Partial<PostHogProviderConfig>) {
    this.config = {
      ...this.config,
      ...config
    };

  }

  capture<T = PostHogCaptureProperties>(name: string, properties?: T, skipCache: boolean = false): void {
    const proxyProperties = {
      ...this.storedProperties,
      ...properties
    };

    if (!skipCache) {
      const cache = this.capturedCache.get(name);

      if (cache && isEqual(cache, proxyProperties)) {
        return;
      }
      this.capturedCache.set(name, proxyProperties);
    }

    if (this.config.alwaysStoreProperties) {
      // Store latest properties
      this.storeProperties(properties);
    }

    this.log(name, proxyProperties);

    if (this.shouldBeTriggered) {
      this.posthog.capture(name, proxyProperties);
    }
  }

  isFeatureEnabled(key: string): boolean {
    return this.posthog.isFeatureEnabled(key);
  }

  getFeatureFlags(): Array<string> {
    return this.posthog.featureFlags.getFlags();
  }

  getFeatureFlag(key: string, postHogOptions = {}): boolean | string | undefined {
    return this.posthog.getFeatureFlag(key);
  }

  identify(id: string, properties?: PostHogCaptureProperties): void {
    this.storeProperties(properties);

    this.log('identify', properties);

    if (!this.shouldBeTriggered || !id) {
      return;
    }

    const gaClientId = this.posthog.get_property('ga_client_id');

    // Identify user
    this.posthog.identify(id, {
      ...properties,
      // Set user property "ga_client_id" if defined
      ...(gaClientId ? { ga_client_id: gaClientId } : {})
    });
  }

  reset(): void {
    this.storedProperties = {};
    this.capturedCache = new Map();

    this.log('reset');

    if (this.shouldBeTriggered) {
      this.posthog.reset();
    }
  }

  setExperimentContext(experimentKey: string, experimentValue: string | boolean): void {
    this.storeProperties({
      experiment_key: experimentKey,
      experiment_value: experimentValue
    });
  }

  private log(name: string, data?): void {
    if (!this.config.isProduction) {
      // tslint:disable-next-line:no-console
      console.info(name, data);
    }
  }

  private storeProperties(properties: PostHogCaptureProperties): void {
    this.storedProperties = {
      ...this.storedProperties,
      ...properties
    };
  }

}
